import fileDownload from 'js-file-download';
import {useState} from 'react';
import type {
  FormFieldFilterRequest,
  FormSubmissionScopeEnum,
  GetListRequestV2Filter,
  GetListRequestV2FilterFn,
  GetListRequestV2Sort,
} from 'src/api/ProactApiClient';
import {useExportFormSubmissionsMutation} from 'src/redux/queries/form';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface FormSubmissionExportTableState {
  filters: GetListRequestV2Filter[];
  filterFns: GetListRequestV2FilterFn[];
  sorting: GetListRequestV2Sort[];
}

export interface FormSubmissionExportScopeArgs {
  formDefinitionId?: number;
  createdBy?: number;
  formFieldFilters?: FormFieldFilterRequest[];
  moduleExtensionFormTabId?: number;
  targetEntityId?: number;
  assetId?: number;
  userId?: number;
}

/**
 * Exports the filtered/sorted rows of a form submission grid as CSV.
 * Server ignores paging (`start`/`length`), so the export always covers the
 * full filtered set, not just the current page. Filename, error copy and
 * request param assembly all live here so every surface stays consistent.
 */
export function useFormSubmissionExport(
  scope: FormSubmissionScopeEnum,
  scopeArgs: FormSubmissionExportScopeArgs,
  tableState: FormSubmissionExportTableState
) {
  const {setAlert} = useAlert();
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormSubmissions] = useExportFormSubmissionsMutation();

  const handleExport = () => {
    const fetch = async () => {
      setIsExporting(true);
      try {
        const res = await exportFormSubmissions({
          scope,
          start: 0,
          length: 0,
          filters: tableState.filters,
          filterFns: tableState.filterFns,
          sorting: tableState.sorting,
          ...scopeArgs,
        }).unwrap();

        if (!res) {
          setAlert('Failed to export file.', 'error');
          return;
        }

        const dateStr = new Date().toISOString().slice(0, 10);
        fileDownload(res.data, `form-submissions-${dateStr}.csv`);
      } catch (error) {
        setAlert(parseErrorMessage(error), 'error');
      } finally {
        setIsExporting(false);
      }
    };

    void fetch();
  };

  return {handleExport, isExporting};
}
